import React, { useRef, useState } from 'react';
import { ActionIcon, Group, TextInput } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import apiClient from '../../services/apiClient.js';
import { setSelectedVehicle } from '../../redux/vehicleSlice.js';

const TopbarSearch = ({ onProModeChange, dark = false }) => {
  const [plateToSearch, setPlateToSearch] = useState('');
  const [isLoading, _setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const setIsLoading = (value) => {
    isLoadingRef.current = value;
    _setIsLoading(value);
  };

  const notify = (cssText, message) => {
    const notification = document.createElement('div');
    notification.style.cssText = cssText;
    notification.innerHTML = message;
    document.body.appendChild(notification);
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  };

  const onSearchForRegoChange = (event) => {
    const value = event.target.value;
    setPlateToSearch(value);

    if (value.toLowerCase() === 'pro mode') {
      localStorage.setItem('proModeUnlocked', 'true');
      onProModeChange(true);
      setPlateToSearch('');
      notify(`
        position: fixed; top: 80px; right: 20px;
        background: linear-gradient(135deg, var(--brand-blue) 0%, var(--brand-blue-deep) 100%);
        color: white; padding: 1rem 1.5rem; border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 10000;
        font-weight: 500; animation: slideIn 0.3s ease-out;
      `, '✓ Pro Mode Activated - Advanced Features Enabled');
    }

    if (value.toLowerCase() === 'standard mode') {
      localStorage.removeItem('proModeUnlocked');
      onProModeChange(false);
      setPlateToSearch('');
      notify(`
        position: fixed; top: 80px; right: 20px;
        background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
        color: white; padding: 1rem 1.5rem; border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.15); z-index: 10000;
        font-weight: 500; animation: slideIn 0.3s ease-out;
      `, '✓ Standard Mode - Pro Features Hidden');
    }
  };

  const onSearchRegoClick = async () => {
    if (!isAuthenticated || !user?.['/roles'] || user['/roles'].length === 0) return;

    const normalizedPlate = (plateToSearch || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!normalizedPlate) {
      alert('Please enter a registration to search.');
      return;
    }

    const accessToken = await getAccessTokenSilently({
      authorizationParams: { audience: process.env.REACT_APP_AUTH0_API_AUDIENCE },
    });
    const userRole = user['/roles'][0];
    const pageBatchSize = Number(process.env.REACT_APP_VEHICLE_HISTORY_BATCH_SIZE || 20) + 1;

    if (!isLoadingRef.current) {
      setIsLoading(true);
      const params = {
        plate: normalizedPlate,
        groupName: userRole,
        pageNo: 1,
        pageBatchSize,
      };

      const result = await apiClient.getVehicleHistoryAsync(params, accessToken);
      setIsLoading(false);
      if (result.length) {
        const selectedVehicle = result.shift();
        dispatch(setSelectedVehicle(selectedVehicle));
        const query = new URLSearchParams();
        if (selectedVehicle.dateTimeUtc) query.set('dateTimeUtc', selectedVehicle.dateTimeUtc);
        query.set('view', 'report');
        navigate(`/vehicle/${normalizedPlate}?${query.toString()}`, {
          state: { vehicle: selectedVehicle, initialView: 'report' },
        });
        return;
      }

      alert(`No vehicle history found for "${normalizedPlate}"`);
    }
  };

  return (
    <Group gap={8} w="100%" maw={360}>
      <TextInput
        placeholder="Search registration..."
        value={plateToSearch}
        onChange={onSearchForRegoChange}
        onKeyDown={(event) => {
          if (event.key === 'Enter') onSearchRegoClick();
        }}
        leftSection={<IconSearch size={16} />}
        style={{ flex: 1 }}
        styles={dark ? {
          input: {
            backgroundColor: '#111827',
            borderColor: 'rgba(148, 163, 184, 0.25)',
            color: '#E5E7EB',
          },
          section: {
            color: '#94A3B8',
          },
        } : undefined}
      />
      <ActionIcon
        variant="filled"
        color={dark ? 'indigo' : 'brand'}
        size="lg"
        loading={isLoading}
        onClick={onSearchRegoClick}
        aria-label="Search registration"
      >
        <IconSearch size={16} />
      </ActionIcon>
    </Group>
  );
};

export default TopbarSearch;
